import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Zap, CheckCircle2, Loader2, Calendar, Briefcase, GraduationCap, AlertCircle, Star } from "lucide-react";
import { toast } from "sonner";

const FILIERE_LABELS = {
    electrotechnique: "BTS Électrotechnique",
};

export default function TutorEvaluation() {
    const { bilanId, token, code: standaloneCode } = useParams();
    const isStandalone = !!standaloneCode && !bilanId;
    const [bilan, setBilan] = useState(null);
    const [standaloneMeta, setStandaloneMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const [form, setForm] = useState({
        tutor_nom: "",
        tutor_prenom: "",
        tutor_fonction: "",
        assiduite: "",
        nb_absences: "",
        missions_realisees: "",
        autonomie: "",
        competences_techniques: "",
        competences_transversales: "",
        ponctualite: 0,
        serieux_motivation: 0,
        integration_equipe: 0,
        respect_delais: 0,
        progression: "",
        difficultes_amelioration: "",
        appreciation_generale: "",
        points_forts: "",
        points_a_ameliorer: "",
        note_globale: "",
        commentaire_ecole: "",
        signature: "",
    });

    const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    useEffect(() => {
        (async () => {
            try {
                if (isStandalone) {
                    // Verify the tutor code is valid by pinging the tutor space
                    const { data } = await api.get(`/public/tutor/space/${standaloneCode}`);
                    setStandaloneMeta({
                        alternant: data.alternant,
                        tutor_code: standaloneCode,
                    });
                } else {
                    const { data } = await api.get(`/public/tutor/${bilanId}/${token}`);
                    setBilan(data);
                    if (data.tutor_evaluation) setDone(true);
                }
            } catch (err) {
                setError(formatApiErrorDetail(err.response?.data?.detail) || "Lien invalide");
            } finally {
                setLoading(false);
            }
        })();
    }, [bilanId, token, standaloneCode, isStandalone]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.tutor_nom || !form.tutor_prenom || !form.signature) {
            toast.error("Nom, prénom et signature sont obligatoires");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                nb_absences: form.nb_absences === "" ? null : Number(form.nb_absences),
                note_globale: form.note_globale === "" ? null : Number(form.note_globale),
                ponctualite: form.ponctualite || null,
                serieux_motivation: form.serieux_motivation || null,
                integration_equipe: form.integration_equipe || null,
                respect_delais: form.respect_delais || null,
            };
            if (isStandalone) {
                await api.post(`/public/tutor/standalone/${standaloneCode}`, payload);
            } else {
                await api.post(`/public/tutor/${bilanId}/${token}`, payload);
            }
            toast.success("Évaluation envoyée à l'établissement !");
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-muted2">Chargement…</div>;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center px-6">
                <div className="panel max-w-md p-8 text-center">
                    <AlertCircle size={32} className="text-destructive mx-auto" />
                    <h1 className="h-display text-2xl font-semibold mt-4">Lien invalide</h1>
                    <p className="text-sm text-muted2 mt-2">Ce lien d'évaluation n'existe pas ou a expiré.</p>
                </div>
            </div>
        );
    }

    if (done) {
        const displayName = isStandalone
            ? `${standaloneMeta?.alternant?.prenom || ""} ${standaloneMeta?.alternant?.nom || ""}`.trim()
            : `${bilan?.prenom || ""} ${bilan?.nom || ""}`.trim();
        return (
            <div className="min-h-screen bg-paper">
                <Header />
                <main className="max-w-2xl mx-auto px-6 py-16">
                    <div className="panel p-10 text-center">
                        <CheckCircle2 size={48} className="text-emerald-600 mx-auto" />
                        <div className="label-eyebrow text-emerald-700 mt-4">Évaluation transmise</div>
                        <h1 className="h-display text-3xl font-semibold mt-2">
                            Merci pour votre évaluation !
                        </h1>
                        <p className="text-sm text-ink/70 mt-4 leading-relaxed">
                            Votre évaluation pour <strong>{displayName}</strong> a bien été enregistrée. L'établissement{isStandalone ? "" : " et l'alternant"} peut désormais la consulter.
                        </p>
                        <p className="text-xs text-muted2 mt-6 font-mono">Vous pouvez fermer cette page.</p>
                    </div>
                </main>
            </div>
        );
    }

    const altInfo = isStandalone ? standaloneMeta?.alternant : bilan;
    const displayName = `${altInfo?.prenom || ""} ${altInfo?.nom || ""}`.trim();

    return (
        <div className="min-h-screen bg-paper">
            <Header />
            <main className="max-w-3xl mx-auto px-6 lg:px-10 py-10" data-testid="tutor-evaluation-page">
                <div className="label-eyebrow mb-2">Espace tuteur · Évaluation d'alternance{isStandalone ? " (libre)" : ""}</div>
                <h1 className="h-display text-4xl font-semibold leading-tight">
                    Évaluez l'alternance de<br />
                    <span className="text-orange">{displayName}</span>
                </h1>
                <p className="text-sm text-muted2 mt-3 max-w-2xl">
                    {isStandalone
                        ? "Vous évaluez l'alternant sans attendre qu'il ait rempli un bilan. Aucun compte requis. Toutes vos réponses seront transmises à l'établissement scolaire."
                        : "Vous accédez à cet espace via un lien partagé par l'alternant. Aucun compte requis. Toutes vos réponses seront transmises à l'établissement scolaire."}
                </p>

                {/* Bilan summary (hidden in standalone mode) */}
                {!isStandalone && bilan && (
                    <div className="panel p-5 mt-6 bg-surface">
                        <div className="label-eyebrow mb-3">Bilan de l'alternant</div>
                        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <div className="flex justify-between"><dt className="text-muted2">Filière</dt><dd>{FILIERE_LABELS[bilan.filiere]}</dd></div>
                            <div className="flex justify-between"><dt className="text-muted2">Niveau</dt><dd>{bilan.niveau === "1ere" ? "1ère année" : "2ème année"}</dd></div>
                            <div className="flex justify-between"><dt className="text-muted2">Entreprise</dt><dd>{bilan.entreprise || "—"}</dd></div>
                            <div className="flex justify-between"><dt className="text-muted2">Tuteur déclaré</dt><dd>{bilan.tuteur}</dd></div>
                            <div className="flex justify-between sm:col-span-2"><dt className="text-muted2">Période</dt><dd className="flex items-center gap-1.5"><Calendar size={13} /> {bilan.date_debut} → {bilan.date_fin}</dd></div>
                        </dl>
                    </div>
                )}

                <form onSubmit={submit} className="space-y-10 mt-10" data-testid="tutor-form">
                    {/* SECTION 1: TUTOR IDENTITY */}
                    <Section number="01" title="Vos informations">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Prénom *">
                                <input className="field-input" required value={form.tutor_prenom} onChange={(e) => upd("tutor_prenom", e.target.value)} data-testid="tutor-prenom" />
                            </Field>
                            <Field label="Nom *">
                                <input className="field-input" required value={form.tutor_nom} onChange={(e) => upd("tutor_nom", e.target.value)} data-testid="tutor-nom" />
                            </Field>
                            <Field label="Fonction dans l'entreprise" className="sm:col-span-2">
                                <input className="field-input" placeholder="Ex: Chef d'équipe maintenance" value={form.tutor_fonction} onChange={(e) => upd("tutor_fonction", e.target.value)} data-testid="tutor-fonction" />
                            </Field>
                        </div>
                    </Section>

                    {/* SECTION 2: ASSIDUITY */}
                    <Section number="02" title="Assiduité & présence">
                        <Field label="L'alternant a-t-il été présent et assidu ?">
                            <div className="grid sm:grid-cols-3 gap-2">
                                {[
                                    { v: "present_no_absence", l: "Toujours présent, aucune absence" },
                                    { v: "present_some", l: "Quelques absences justifiées" },
                                    { v: "absences_concerning", l: "Absences préoccupantes" },
                                ].map((o) => (
                                    <button
                                        type="button"
                                        key={o.v}
                                        onClick={() => upd("assiduite", o.v)}
                                        className={`p-3 border rounded-sm text-left text-sm ${form.assiduite === o.v ? "border-orange bg-orange/5" : "border-rule hover:border-ink/40"}`}
                                        data-testid={`assiduite-${o.v}`}
                                    >
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Nombre d'absences (optionnel)">
                            <input type="number" min="0" max="20" className="field-input max-w-[160px]" value={form.nb_absences} onChange={(e) => upd("nb_absences", e.target.value)} placeholder="0" data-testid="tutor-nb-absences" />
                        </Field>
                    </Section>

                    {/* SECTION 3: ACTIVITIES & AUTONOMY */}
                    <Section number="03" title="Activités réalisées & autonomie">
                        <Field label="Missions confiées et projets réalisés *">
                            <textarea rows={5} className="field-input resize-none" value={form.missions_realisees} onChange={(e) => upd("missions_realisees", e.target.value)} placeholder="Décrivez les missions effectuées par l'alternant…" data-testid="tutor-missions" />
                        </Field>
                        <Field label="Niveau d'autonomie observé">
                            <div className="grid sm:grid-cols-3 gap-2">
                                {[
                                    { v: "assiste", l: "Doit être assisté en permanence" },
                                    { v: "encadre", l: "Encadré, demande ponctuellement" },
                                    { v: "autonome", l: "Travaille en totale autonomie" },
                                ].map((o) => (
                                    <button
                                        type="button"
                                        key={o.v}
                                        onClick={() => upd("autonomie", o.v)}
                                        className={`p-3 border rounded-sm text-left text-sm ${form.autonomie === o.v ? "border-orange bg-orange/5" : "border-rule hover:border-ink/40"}`}
                                        data-testid={`autonomie-${o.v}`}
                                    >
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    </Section>

                    {/* SECTION 4: SKILLS DEVELOPED */}
                    <Section number="04" title="Compétences développées">
                        <Field label="Compétences techniques (logiciels, outils, méthodes)">
                            <textarea rows={4} className="field-input resize-none" value={form.competences_techniques} onChange={(e) => upd("competences_techniques", e.target.value)} placeholder="Ex: Lecture de schémas électriques, mise en service Schneider Unity Pro…" data-testid="tutor-competences-techniques" />
                        </Field>
                        <Field label="Compétences transversales (organisation, communication, équipe)">
                            <textarea rows={4} className="field-input resize-none" value={form.competences_transversales} onChange={(e) => upd("competences_transversales", e.target.value)} placeholder="Ex: A su prendre des initiatives, bonne communication client…" data-testid="tutor-competences-transversales" />
                        </Field>
                    </Section>

                    {/* SECTION 5: BEHAVIORAL EVALUATION */}
                    <Section number="05" title="Comportement professionnel (notes /5)">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <RatingField label="Ponctualité" value={form.ponctualite} onChange={(v) => upd("ponctualite", v)} testId="rating-ponctualite" />
                            <RatingField label="Sérieux & motivation" value={form.serieux_motivation} onChange={(v) => upd("serieux_motivation", v)} testId="rating-serieux" />
                            <RatingField label="Intégration dans l'équipe" value={form.integration_equipe} onChange={(v) => upd("integration_equipe", v)} testId="rating-integration" />
                            <RatingField label="Respect des consignes & délais" value={form.respect_delais} onChange={(v) => upd("respect_delais", v)} testId="rating-delais" />
                        </div>
                    </Section>

                    {/* SECTION 6: PROGRESSION */}
                    <Section number="06" title="Progression de l'alternant">
                        <Field label="Ce que l'alternant sait mieux faire qu'au début">
                            <textarea rows={3} className="field-input resize-none" value={form.progression} onChange={(e) => upd("progression", e.target.value)} data-testid="tutor-progression" />
                        </Field>
                        <Field label="Difficultés rencontrées et points d'amélioration">
                            <textarea rows={3} className="field-input resize-none" value={form.difficultes_amelioration} onChange={(e) => upd("difficultes_amelioration", e.target.value)} data-testid="tutor-difficultes" />
                        </Field>
                    </Section>

                    {/* SECTION 7: GLOBAL OPINION */}
                    <Section number="07" title="Avis global du tuteur">
                        <Field label="Appréciation générale">
                            <textarea rows={4} className="field-input resize-none" value={form.appreciation_generale} onChange={(e) => upd("appreciation_generale", e.target.value)} data-testid="tutor-appreciation" />
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Points forts">
                                <textarea rows={3} className="field-input resize-none" value={form.points_forts} onChange={(e) => upd("points_forts", e.target.value)} data-testid="tutor-points-forts" />
                            </Field>
                            <Field label="Points à améliorer">
                                <textarea rows={3} className="field-input resize-none" value={form.points_a_ameliorer} onChange={(e) => upd("points_a_ameliorer", e.target.value)} data-testid="tutor-points-ameliorer" />
                            </Field>
                        </div>
                        <Field label="Note globale /20 (optionnel)">
                            <input type="number" min="0" max="20" className="field-input max-w-[160px]" value={form.note_globale} onChange={(e) => upd("note_globale", e.target.value)} placeholder="ex: 16" data-testid="tutor-note-globale" />
                        </Field>
                        <Field label="Commentaire pour l'établissement / le CFA (optionnel)">
                            <textarea rows={3} className="field-input resize-none" value={form.commentaire_ecole} onChange={(e) => upd("commentaire_ecole", e.target.value)} data-testid="tutor-commentaire-ecole" />
                        </Field>
                    </Section>

                    {/* SECTION 8: SIGNATURE */}
                    <Section number="08" title="Validation & signature">
                        <p className="text-xs text-muted2 mb-3">
                            En tapant votre nom complet ci-dessous, vous validez l'authenticité de cette évaluation.
                            Cela tient lieu de signature électronique.
                        </p>
                        <Field label="Signature (votre nom complet) *">
                            <input
                                className="field-input font-mono"
                                required
                                value={form.signature}
                                onChange={(e) => upd("signature", e.target.value)}
                                placeholder={`${form.tutor_prenom} ${form.tutor_nom}`.trim() || "Prénom NOM"}
                                data-testid="tutor-signature"
                            />
                        </Field>
                    </Section>

                    <div className="flex items-center justify-end gap-3 pt-4">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary !py-3 !px-7"
                            data-testid="tutor-submit-button"
                        >
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            {submitting ? "Envoi en cours…" : "Valider et envoyer mon évaluation"}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}

const Header = () => (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-rule">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
            <Link to="/" className="flex items-center gap-2.5">
                <span className="w-7 h-7 bg-ink text-orange flex items-center justify-center">
                    <Zap size={16} strokeWidth={2.5} />
                </span>
                <span className="h-display text-lg font-semibold tracking-tight">
                    Bilan<span className="text-orange">BTS</span>
                </span>
            </Link>
            <span className="pill bg-orange-50 text-orange-700 border border-orange-200 inline-flex items-center">
                <Briefcase size={11} className="mr-1" /> Espace Tuteur
            </span>
        </div>
    </header>
);

const Section = ({ number, title, children }) => (
    <section className="space-y-4">
        <div className="flex items-baseline gap-3 pb-3 border-b border-rule">
            <span className="text-xs font-mono text-orange font-bold">{number}</span>
            <h2 className="h-display text-xl font-semibold">{title}</h2>
        </div>
        <div className="space-y-4">{children}</div>
    </section>
);

const Field = ({ label, children, className = "" }) => (
    <div className={className}>
        <label className="label-eyebrow mb-2 block">{label}</label>
        {children}
    </div>
);

const RatingField = ({ label, value, onChange, testId }) => (
    <div>
        <label className="label-eyebrow mb-2 block">{label}</label>
        <div className="flex gap-1.5" role="radiogroup" data-testid={testId}>
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    type="button"
                    key={n}
                    onClick={() => onChange(value === n ? 0 : n)}
                    className={`w-9 h-9 border rounded-sm flex items-center justify-center transition-colors ${
                        n <= value
                            ? "bg-orange border-orange text-white"
                            : "border-rule hover:border-orange text-muted2"
                    }`}
                    aria-label={`${n} sur 5`}
                >
                    <Star size={14} fill={n <= value ? "currentColor" : "none"} />
                </button>
            ))}
            {value > 0 && <span className="ml-2 text-xs font-mono text-muted2 self-center">{value}/5</span>}
        </div>
    </div>
);
